import React, { useState } from 'react';
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';
import classes from './BestTimeToPostChart.module.scss';
import { TimeFormatSetting, TimezoneSetting } from '../../../../../models/graphql';
import { ChartTooltipContainer } from '../../../LineChart/ChartTooltipContainer';
import { useSelectedOrganization } from '../../../../../hooks/useSelectedOrganization';
import { formatTimezoneToDisplay } from '../../../../../helpers/timeFormatter';
import { ChartPanel } from '../../../ChartPanel';
import {
  BestTimesType,
  ChartDataByHours,
  ChartDataByWeek,
  formatTickTo12h,
  formatTickTo24h
} from './BestTimeToPostChart.controller';
import { Breakpoints, useBreakpoints } from '../../../../../hooks/useBreakpoints';
import { CHART_STROKE_COLOR, TEXT_COLOR_FOR_CHART_SVG } from '../../../chartConsts';
import { Tooltip } from 'components/actions/Tooltip';

type CharBars = {
  value?: (string | number)[] | string | number;
  payload?: { weekDay?: string; engagement: number; hour?: string };
}[];
type TooltipBestTimeChartProps = {
  active?: boolean;
  payload?: CharBars;
  timeZone: string;
  hourFormat: TimeFormatSetting;
};
type CustomBarShapeProps = {
  bestTimeObj: BestTimesType;
  active: boolean;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  engagement?: number | null;
};
type BestHourChartTickProps = {
  x: number;
  y: number;
  index: number;
  payload: {
    value: string;
  };
  visibleTicksCount: number;
  tickFormatter: (hour: number | string) => string;
};
const CustomBarShape = (props: CustomBarShapeProps) => {
  const { engagement, active, bestTimeObj } = props;
  const x = props.x ?? 0;
  const y = props.y ?? 0;
  const width = props.width ?? 0;
  const height = props.height ?? 0;

  let opacity = 0.5;

  if (bestTimeObj?.bestTime?.value === engagement) {
    opacity = 1;
  }
  if (bestTimeObj?.secondBestTime?.value === engagement || bestTimeObj?.thirdBestTime?.value === engagement) {
    opacity = 0.7;
  }
  if (engagement === null) {
    const centerX = x + width / 2;
    const centerY = y + height / 2;

    return (
      <svg
        x={centerX - 8}
        y={centerY - 20}
        width={'18'}
        height={'18'}
        xmlns={'http://www.w3.org/2000/svg'}
        className={active ? classes.NoDataActive : classes.NoData}>
        <path
          fillRule={'evenodd'}
          clipRule={'evenodd'}
          d={
            'M9 2.0625C5.17066 2.0625 2.0625 5.17066 2.0625 9C2.0625 10.7124 2.68347 12.2801 3.71352 13.491L13.491 3.71352C12.2801 2.68347 10.7124 2.0625 9 2.0625ZM0.9375 9C0.9375 4.54934 4.54934 0.9375 9 0.9375C11.2253 0.9375 13.2417 1.84119 14.7003 3.29975C14.8057 3.40524 14.865 3.54832 14.865 3.6975C14.865 3.84668 14.8057 3.98976 14.7003 4.09525L4.09524 14.7002C3.87557 14.9199 3.51942 14.9199 3.29975 14.7002C1.84119 13.2417 0.9375 11.2253 0.9375 9Z'
          }
        />
        <path
          fillRule={'evenodd'}
          clipRule={'evenodd'}
          d={
            'M13.9065 3.30147C14.1262 3.0818 14.4823 3.0818 14.702 3.30147C16.1605 4.76003 17.0642 6.77639 17.0642 9.00172C17.0642 13.4524 13.4524 17.0642 9.00172 17.0642C6.77639 17.0642 4.76003 16.1605 3.30147 14.702C3.19598 14.5965 3.13672 14.4534 3.13672 14.3042C3.13672 14.155 3.19598 14.012 3.30147 13.9065L13.9065 3.30147ZM4.51074 14.2882C5.72167 15.3182 7.2893 15.9392 9.00172 15.9392C12.8311 15.9392 15.9392 12.8311 15.9392 9.00172C15.9392 7.28929 15.3183 5.72167 14.2882 4.51074L4.51074 14.2882Z'
          }
        />
      </svg>
    );
  }
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      className={active ? classes.BarActive : classes.BarColor}
      opacity={active ? 1 : opacity}
    />
  );
};

const TooltipChart = (props: TooltipBestTimeChartProps) => {
  const { active, payload, timeZone, hourFormat } = props;
  if (active && payload && payload.length) {
    const hour = payload[0].payload?.hour ?? '';
    const engagementValue =
      typeof payload[0].payload?.engagement === 'number' ? payload[0].payload?.engagement : 'No data available';
    const nextHour = `${parseInt(hour) + 1 === 24 ? 0 : parseInt(hour) + 1}`;
    const hourString =
      hourFormat === TimeFormatSetting.HOUR_12
        ? `${formatTickTo12h(hour)} - ${formatTickTo12h(nextHour)}`
        : `${formatTickTo24h(hour)} - ${formatTickTo24h(nextHour)}`;
    const tooltipTitle = payload[0].payload?.weekDay ?? `${hourString} (${timeZone})`;
    const formattedEngagement = engagementValue.toLocaleString('en-US');

    return (
      <ChartTooltipContainer>
        <h5>{tooltipTitle}</h5>
        <div className={classes.ValueContainer}>
          <p>Total Engagement</p>
          <p>{formattedEngagement}</p>
        </div>
      </ChartTooltipContainer>
    );
  }
  return null;
};

const BestHourChartTick = (props: BestHourChartTickProps) => {
  const { x, y, payload, index, tickFormatter } = props;

  if (index % 3 !== 0) {
    return <></>;
  }
  const value = payload.value;
  return (
    <g transform={`translate(${x},${y})`}>
      <text opacity={0.85} textAnchor={'middle'} x={0} y={0} dy={16} className={classes.TickLabelText}>
        {tickFormatter(parseInt(value) + 1)}
      </text>
    </g>
  );
};

type BestTimeToPostChartViewProps = {
  bestTimeObj: BestTimesType;
  chartByHours: ChartDataByHours[] | null;
  chartByWeekday: ChartDataByWeek[] | null;
  hourFormat: TimeFormatSetting;
  bestDayFormattedString?: string | null;
  loading: boolean;
  isEmpty: boolean;
};

export const BestTimeToPostChartView = ({
  bestTimeObj,
  chartByWeekday,
  chartByHours,
  hourFormat,
  bestDayFormattedString,
  loading,
  isEmpty
}: BestTimeToPostChartViewProps) => {
  const [activeWeekDay, setActiveWeekday] = useState<string | null>(null);
  const breakpoint = useBreakpoints();
  const { selectedWorkspace } = useSelectedOrganization();
  const wsTimezone = selectedWorkspace?.timezone ?? TimezoneSetting.UTC;
  const formattedTimezone = formatTimezoneToDisplay(wsTimezone);
  const { bestTime, secondBestTime, thirdBestTime } = bestTimeObj;
  const maxEngagementByWeek = chartByWeekday ? Math.max(...chartByWeekday.map(data => data.engagement ?? 0)) : 0;
  const maxEngagementByHour = chartByHours ? Math.max(...chartByHours.map(data => data.engagement ?? 0)) : 0;
  const getBarSize = (breakpoint: Breakpoints) => {
    const barSizes = {
      tabletportrait: { week: 70, hour: 16 },
      large: { week: 97, hour: 23 },
      xlarge: { week: 116, hour: 28 },
      xxlarge: { week: 122, hour: 30 },
      default: { week: 0, hour: 0 }
    };
    if (breakpoint === 'small' || breakpoint === 'medium') {
      return barSizes['default'];
    }
    return barSizes[breakpoint];
  };
  const weekDayChartPadding = breakpoint === 'tabletportrait' ? 30 : 60;
  const hoursChartPadding = breakpoint === 'tabletportrait' ? 12 : 24;

  const onMouseEnter = (weekDay: string) => {
    setActiveWeekday(weekDay);
  };
  const onMouseLeave = () => {
    setActiveWeekday(null);
  };

  return (
    <div className={'cell'}>
      <ChartPanel
        loading={loading}
        chartTitle={'Best time to post'}
        chartSubtitle={'How is it calculated'}
        subTitleTooltipText={'We analyze all posts published in this date range, and work out how much engagement you got on each day, or each hour.'}
        isEmpty={isEmpty}>
        <div className={classes.BestTimeContainer}>
          <p>
            {`${bestTime?.hourKey} on `} <span>{bestDayFormattedString}s</span> (All times are in {formattedTimezone}{' '}
            timezone)
          </p>
          <p>
            {`This looks like the best time for you to maximize your reach. You can also try ${secondBestTime?.hourKey} and ${thirdBestTime?.hourKey} on ${bestDayFormattedString}
          too.`}
          </p>
        </div>
        {chartByWeekday && (
          <>
            <h3 className={'margin-bottom-small-22'}>Best day of the week</h3>
            <ResponsiveContainer width={'100%'} height={200}>
              <BarChart barSize={getBarSize(breakpoint).week} data={chartByWeekday}>
                <YAxis hide domain={[0, maxEngagementByWeek]} />
                <XAxis
                  padding={{ left: weekDayChartPadding, right: weekDayChartPadding }}
                  scale={'point'}
                  tickMargin={8}
                  dataKey={'weekDay'}
                  axisLine={{ stroke: CHART_STROKE_COLOR }}
                  tick={{ fill: TEXT_COLOR_FOR_CHART_SVG, opacity: 0.85 }}
                  tickLine={false}
                />
                <Bar dataKey={'engagement'} className={classes.BarColorWeek}>
                  {chartByWeekday.map(({ weekDay, engagement }) => (
                    <Cell
                      key={weekDay}
                      opacity={engagement === maxEngagementByWeek || activeWeekDay === weekDay ? 1 : 0.5}
                      className={activeWeekDay === weekDay ? classes.BarColorWeekActive : undefined}
                      onMouseEnter={() => onMouseEnter(weekDay)}
                      onMouseLeave={onMouseLeave}
                    />
                  ))}
                </Bar>
                <RechartsTooltip
                  cursor={false}
                  filterNull={false}
                  content={props => <TooltipChart {...props} timeZone={formattedTimezone} hourFormat={hourFormat} />}
                />
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
        {chartByHours && (
          <>
            <div className={classes.BestHourTitleContainer}>
              <h3>Best hour of the day</h3>
              <Tooltip pointer type={'inlined'} target={'#bestDayWeekTooltip'}>
                {`All times are in ${formattedTimezone} timezone`}
              </Tooltip>
            </div>

            <ResponsiveContainer className={classes.BestByHourContainer} height={200}>
              <BarChart barSize={getBarSize(breakpoint).hour} data={chartByHours}>
                <YAxis domain={[0, maxEngagementByHour]} hide={true} />
                <XAxis
                  padding={{ left: hoursChartPadding, right: hoursChartPadding - 4 }}
                  scale={'point'}
                  dataKey={'hour'}
                  tickLine={true}
                  interval={0}
                  tick={BestHourChartTick}
                  tickFormatter={hourFormat === TimeFormatSetting.HOUR_12 ? formatTickTo12h : formatTickTo24h}
                  axisLine={{ stroke: CHART_STROKE_COLOR }}
                />
                <Bar
                  dataKey={'engagement'}
                  className={classes.BarColor}
                  shape={<CustomBarShape bestTimeObj={bestTimeObj} active={false} />}
                  activeBar={<CustomBarShape active={true} bestTimeObj={bestTimeObj} />}
                />
                <RechartsTooltip
                  cursor={false}
                  filterNull={false}
                  content={props => <TooltipChart {...props} timeZone={formattedTimezone} hourFormat={hourFormat} />}
                />
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </ChartPanel>
    </div>
  );
};
